import CryptoJS from 'crypto-js';
import _ from 'lodash';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
    APP_IMG,
    GTM_ID,
    IMAGE_DEFAULT,
    IS_NO_ROBOT_INDEX,
    SUPER_SECRET_KEY,
    URL_MAIN,
    WEB_DESC,
    WEB_KEYWORD,
    WEB_NAME,
    WEB_TITLE,
    WEB_URL
} from 'src/constants';
import { convertDatasets, convertObjPath, timestamp } from 'src/utils/helper';
import { modifiedData, modifiedMergeData } from 'src/utils/modified';
import { redisDB0, redisDB1, redisDB15 } from 'src/utils/redis';
import { GET, GET_REDIS } from 'src/utils/services';

const Layout = dynamic(import('src/components/layouts'));
const SectionOne = dynamic(import('src/components/section/section-home/section-one'));
const SectionIndex = dynamic(import('src/components/section/section-index'));

const PageIndex = ({
  background,
  dataAll,
  hideFooter,
  ads,
  showData,
  showAds
}) => {
  const [height, setHeight] = useState(0);
  useEffect(() => {
    setTimeout(() => {
      setHeight(document.getElementById('body').offsetHeight);
      window.parent.postMessage(height, URL_MAIN);
    }, 100);
  }, [height]);

  const _background = {
    title: convertDatasets(background?.dataSet, 'title', true),
    description: convertDatasets(background?.dataSet, 'description', true),
    keywords: convertDatasets(background?.dataSet, 'keywords', true),
    image: convertDatasets(background?.dataSet, 'image', true),
    desktop: _.find(background?.dataSet, (el) => el?.key === 'background-desktop')?.value,
    mobile: _.find(background?.dataSet, (el) => el?.key === 'background-mobile')?.value
  };

  return (
    <>
      {/* -------------------------------- NEXT_SEO -------------------------------- */}
      <NextSeo
        noindex={IS_NO_ROBOT_INDEX}
        nofollow={IS_NO_ROBOT_INDEX}
        title={_background?.title || WEB_TITLE}
        description={_background?.description || WEB_DESC}
        openGraph={{
          type: 'article',
          locale: 'th_TH',
          url: WEB_URL.WEB,
          title: _background?.title || WEB_TITLE,
          description: _background?.description || WEB_DESC,
          images: [
            {
              url: _background?.image || IMAGE_DEFAULT,
              width: '1920',
              height: '1080',
              alt: WEB_NAME
            }
          ]
        }}
        additionalMetaTags={[
          {
            name: 'keywords',
            content: _background?.keywords || WEB_KEYWORD
          }
        ]}
      />
      <Head>
        <link rel='canonical' href={WEB_URL.WEB} />
        <meta property='twitter:title' content={_background?.title || WEB_TITLE} />
        <meta property='twitter:description' content={_background?.description || WEB_DESC} />
        <meta property='twitter:image' content={_background?.image || IMAGE_DEFAULT} />

        {/* ----------------------------- Google Analytic ---------------------------- */}
        <script
          type='text/javascript'
          dangerouslySetInnerHTML={{
            __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({
            'gtm.start':new Date().getTime(),event:'gtm.js'});
            var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';
            j.async=true;
            j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','${GTM_ID}');`
          }}
        />
        {/* ---------------------------- SPIPPET : WebSite --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                  {
                    "@context": "https://schema.org",
                    "@type": "WebSite",
                    "name": "${WEB_NAME}",
                    "url": "${WEB_URL.WEB}",
                    "description": "${_background?.description || WEB_DESC}",
                    "sameAs": [
                      "${WEB_URL.FACEBOOK}",
                      "${WEB_URL.X}",
                      "${WEB_URL.INSTAGRAM}",
                      "${WEB_URL.YOUTUBE}",
                      "${WEB_URL.LINE}"
                    ],
                    "potentialAction": {
                      "@type": "SearchAction",
                      "target": "${WEB_URL.WEB}search?q={search_term_string}",
                      "query-input": "required name=search_term_string"
                    }
                  }
                }
              `
          }}
        />
        {/* ---------------------------- SPIPPET : WebPage --------------------------- */}
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: `
                  {
                    "@context": "http://schema.org",
                    "@type": "WebPage",
                    "name": "${WEB_NAME}",
                    "description": "${_background?.description || WEB_DESC}",
                    "publisher": {
                      "@type": "ProfilePage",
                      "name": "${WEB_NAME}"
                    }
                  }
                `
          }}
        />
      </Head>
      <Layout dataset={dataAll} data={background} hideFooter={hideFooter}>
        <h1>{_background?.title || WEB_TITLE}</h1>
        <h2>{_background?.description || WEB_DESC}</h2>
        <PageWrapper bgDesktop={_background?.desktop} bgMobile={_background?.mobile || _background?.desktop}>
          <SectionOne id='section-1' className='section-1' data={_.find(dataAll, { topic: 'section-1' })} showData={showData} ads={ads} showAds={showAds} />
          <SectionIndex id='section-2' className='section-2' data={_.find(dataAll, { topic: 'section-2' })}  ads={ads} showAds={showAds} />
        </PageWrapper>
      </Layout>
    </>
  );
};

const PageWrapper = styled.div`
  width: 100vw;
  background-image: ${props => props.bgDesktop ? `url(${props.bgDesktop})` : 'none'};
  background-size: cover;
  background-position: bottom;
  @media (max-width: 768px) {
    background-image: ${props => props.bgMobile ? `url(${props.bgMobile})` : 'none'};
  }
`;

export async function getServerSideProps ({ query }) {
  const initProps = {
    statusCode: 200,
    errMessage: null,
    categories: [],
    votes: [],
    items: null,
    covers: [],
    hideFooter: null,
    hideCover: null,
    background: {},
    dataAll: [],
    sectionVote: {},
    showAds: false,
    ga: null,
    isCountdown: false,
    remainingTime: null,
    showData: [],
    ads:[],
    dataCate: []
  };
  try {
    const categoriesData = await GET_REDIS('redisDB0');
    const scoreData = await GET_REDIS('redisDB4', true);
    const itemsVotesData = await GET_REDIS('redisDB6');
    if (!_.isEmpty(query?.hideFooter) || !_.isEmpty(query?.hideCover)) {
      initProps.hideFooter = String(query?.hideFooter) === '1' || null;
      initProps.hideCover = String(query?.hideCover) === '1' || null;
    }
    if (!_.isEmpty(query?.ga)) {
      initProps.ga = query.ga;
    }
    const countdown = await redisDB15.hmget(query?.ga, 'ga', query?.ga);
    if (!_.isEmpty(countdown[0])) {
      initProps.isCountdown = true;
      const remainingTime = await redisDB15.ttl(query?.ga);
      initProps.remainingTime = remainingTime;
    }
    const coverData = await GET('/api/v1.0/imagecover');
    if (!_.isEmpty(coverData)) initProps.covers = coverData || [];

    const dataSetsAll = await GET(`/api/datasets${convertObjPath({ limit: 40 })}`);
    /* --------------------------------- DataSet -------------------------------- */
    if (!_.isEmpty(dataSetsAll)) {
      initProps.showData = !_.isEmpty(_.find(dataSetsAll, { topic: 'section-schedule' }));
      initProps.dataAll = dataSetsAll;
      initProps.background = _.find(dataSetsAll, { topic: 'background' }) || {};
      initProps.showAds = !_.isEmpty(_.find(dataSetsAll, { topic: 'show-ads' }));
    }

    /* ---------------------------------- REDIS --------------------------------- */
    const dataSecret = {
      username: '$}25tQg.ctj@7C.2r(j+R.;KNF3ERT',
      password: 'M:]?-@Gnd#/M*ZEM(G%eY5]qfg7p_W'
    };
    initProps.key = CryptoJS.AES.encrypt(JSON.stringify(dataSecret), SUPER_SECRET_KEY).toString();

    // ========================= Categories
    const allCategories = await redisDB0.keys('*', (err, result) => {
      if (err) {
        return null;
      }
      return result;
    });

    const itemGroup = [];
    for (let i = 0; i < allCategories.length; i++) {
      const _el = allCategories.sort();
      const _index = _el[i].split('_')[1];
      itemGroup.push(redisDB1.hgetall(`items:group_${_index}`));
    }

    const itemsData = await Promise.all(itemGroup);
    const itemsAll = itemsData.map((item) => {
      const valueData = Object.values(item).map((item) => item);
      return valueData;
    });
    // ========================= Merge Items
    const mergeItems = itemsAll.flat();
    const unSorted = mergeItems.map((item, index) => {
      const newKeyObj = { key: index + 1 };
      const newLabelObj = { label: JSON.parse(item)?.name };

      const newValueObj = { value: item };
      const mergeObj = Object.assign({}, newKeyObj, newLabelObj, newValueObj);
      return mergeObj;
    });
    const sorted = unSorted.sort(function (a, b) {
      return a.label.trim().localeCompare(b.label.trim());
    });
    initProps.items = sorted;

    initProps.categories = modifiedData(categoriesData) || [];
    initProps.votes = modifiedMergeData(itemsVotesData, modifiedData(scoreData)) || [];


    const adsLists = await GET(`${APP_IMG}/awards/ads-slots-homepage.json`,true);
    // console.log('adsLists ', adsLists);
    initProps.ads = adsLists || [];

  } catch (err) {
    console.error(`${timestamp()} ==========> PAGE_INDEX ERROR : `, err);
  }

  return {
    props: { ...initProps }
  };
}

export default PageIndex;
